import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../../MainDrawer";
import APIGatewayRequestsTable from "./APIGatewayRequestsTable";
import MiniBot from "../../MiniBot";
import queryGraphql from "../../../helpers/ApiInterface"; // Added API helper

// Define the type for a Gateway object (shape the component expects)
interface Gateway {
    id: string;
    name: string;
    apiType: string; // Changed to string to accept API data
    endpointType: string; // Changed to string
    userPoolName: string;
    region: string;
    wafEnabled: boolean;
    tokenHeaderSource: string;
    status: string; // Changed to string
    solutionOwner: string;
    createdAt: string;
    googleProfilePicture?: string
}

// Define the type for search options
interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof Gateway | string;
    groupBy: string;
}

// Hardcoded data for API Gateways - REMOVED

let searchOptions: SearchOptions[] = [];

const APIGatewayRequestsPage: React.FC = () => {
    const [gateways, setGateways] = useState<Gateway[]>([]); // Initialize with empty array
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    
    useEffect(() => {
        // Function to fetch and process gateway data
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetAPIGateways {
                      getAPIGateways {
                        id
                        name
                        apiGatewayType
                        apiEndpointType
                        region
                        userPoolName
                        tokenHeaderSource
                        awsWafEnabled
                        status
                        createdAt
                        updatedAt
                        reasonForRejection
                        apiGatewayOwner {
                          id
                          name
                          googleProfilePicture
                        }
                          apiGatewayApprover { 
                        id
                        name
                        googleProfilePicture
                        }
                      }
                    }
                `);

                const responseData = data.getAPIGateways || [];

                // Transform API data to the component's expected 'Gateway' interface
                const mappedGateways: Gateway[] = responseData.map((gw: any) => ({
                    id: gw.id,
                    name: gw.name,
                    apiType: gw.apiGatewayType,
                    endpointType: gw.apiEndpointType,
                    userPoolName: gw.userPoolName,
                    region: gw.region,
                    wafEnabled: gw.awsWafEnabled,
                    tokenHeaderSource: gw.tokenHeaderSource,
                    status: gw.status.toLowerCase(), // Ensure status is lowercase for filtering
                    solutionOwner: gw.apiGatewayOwner?.name || 'N/A', // Map nested name
                    createdAt: new Date(Number(gw.createdAt)).toISOString(), // Format timestamp
                    googleProfilePicture: gw.apiGatewayOwner?.googleProfilePicture,
                    actionedAt: gw.updatedAt ? new Date(Number(gw.updatedAt)).toISOString() : null,
                    apiApproverName: gw.apiGatewayApprover?.name,
                    apiApproverAvatar: gw.apiGatewayApprover?.googleProfilePicture,
                    reasonForRejection: gw.reasonForRejection,
                }));

                // Set gateway data from the fetched array
                setGateways(mappedGateways);

                // Generate search options from the fetched gateway data
                let options: SearchOptions[] = mappedGateways
                    .map((gw: Gateway) => {
                        const gwOptions: SearchOptions[] = [
                            { category: "name", groupBy: "GATEWAY NAME", chipLabel: `Name: ${gw.name}`, dropdownLabel: gw.name },
                            { category: "solutionOwner", groupBy: "OWNER", chipLabel: `Owner: ${gw.solutionOwner}`, dropdownLabel: gw.solutionOwner },
                            { category: "apiType", groupBy: "API TYPE", chipLabel: `Type: ${gw.apiType}`, dropdownLabel: gw.apiType },
                            { category: "region", groupBy: "REGION", chipLabel: `Region: ${gw.region}`, dropdownLabel: gw.region },
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${gw.status}`, dropdownLabel: gw.status },
                        ];
                        return gwOptions;
                    })
                    .flat();

                // Remove duplicate search options and sort them
                searchOptions = Array.from(new Map(options.map(item => [`${item.category}:${item.dropdownLabel}`, item])).values());
                searchOptions.sort((a, b) => a.groupBy.localeCompare(b.groupBy) || a.dropdownLabel.localeCompare(b.dropdownLabel));
            
            } catch (error) {
                console.error("Error fetching API Gateways:", error);
                setGateways([]); // Ensure state is empty on error
            }
        };

        fetchData(); // Call the fetch function
    }, []); // Run only once on component mount

    // Filter gateways based on the selected search criteria
    const filteredGateways =
        searchQuery.length === 0
            ? gateways
            : gateways.filter((gateway: any) =>
                searchQuery.every(filter => {
                    const gatewayValue = gateway[filter.category]?.toLowerCase();
                    const filterValue = filter.dropdownLabel?.toLowerCase();
                    // This logic was slightly modified to correctly match categories
                    const categoryFilters = searchQuery.filter(f => f.category === filter.category);
                    const categoryValues = categoryFilters.map(f => f.dropdownLabel.toLowerCase());
                    return categoryValues.includes(gatewayValue);
                })
            );
    
    const uniqueFilteredGateways = Array.from(new Map(filteredGateways.map(gw => [gw.id, gw])).values());

    const filteredActiveGateways = uniqueFilteredGateways.filter(gw => gw.status === "active");
    const filteredPendingGateways = uniqueFilteredGateways.filter(gw => gw.status === "pending");
    const filteredRejectedGateways = uniqueFilteredGateways.filter(gw => gw.status === "rejected");


    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-apis-gateway"} />
            <MiniBot/>
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: "100vh" }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 1, mx: 2 }}>
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Requests</Typography>
                            <IconButton aria-label="Help" onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")} sx={{ p: 0, border: 'none', mr:2 }}>
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40 }}/>
                            </IconButton>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                        <Box>
                            <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">{`API Gateways (${gateways.length})`}</Typography>
                            <Typography fontFamily="Gilroy-Regular" fontSize="14px">{`View all the requests raised for API Gateways`}</Typography>
                        </Box>
                        <Autocomplete
                            multiple
                            options={searchOptions}
                            filterSelectedOptions
                            getOptionLabel={(option) => option.dropdownLabel}
                            groupBy={(option) => option.groupBy}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />)
                            }
                            onChange={(_, newVal) => setSearchQuery(newVal)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    placeholder="Search by name, owner, status etc"
                                    fullWidth
                                    size="small"
                                    hiddenLabel
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", paddingLeft: "4px" }}/>
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        disableUnderline: true,
                                        style: {
                                            backgroundColor: "#f4f4f4",
                                            minHeight: "45px",
                                            width: "500px",
                                            borderRadius: "8px",
                                            fontFamily: "Gilroy-Regular",
                                            fontSize: "14px",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion defaultExpanded={filteredPendingGateways.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: 2, '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredPendingGateways.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending Gateways ({filteredPendingGateways.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><APIGatewayRequestsTable gateways={filteredPendingGateways} /></AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: 2, '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredActiveGateways.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Active" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Active Gateways ({filteredActiveGateways.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><APIGatewayRequestsTable gateways={filteredActiveGateways} /></AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={filteredRejectedGateways.length > 0} elevation={0} sx={{ borderRadius: "8px", '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredRejectedGateways.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected Gateways ({filteredRejectedGateways.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><APIGatewayRequestsTable gateways={filteredRejectedGateways} /></AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default APIGatewayRequestsPage;